import { createContext, useCallback, useContext, useEffect, useState, PropsWithChildren, useRef } from 'react';
import supabaseClient from '@/api/supabaseClient.ts';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import useLogin from '@/api/useLogin.ts';
import useSendResetPasswordLink from '@/api/useSendResetPasswordLink.ts';
import useResetPassword from '@/api/useResetPassword.ts';
import useVerifyEmail from '@/api/useVerifyEmail.ts';
import useResendVerificationCode from '@/api/useResendVerificationCode.ts';
import { User, UserType } from '@/types/auth.ts';

interface AuthContextProps {

  session: Session | null;
  supabaseClient: SupabaseClient;
  logout: () => void;
  isLoading: boolean;
  loginQuery: ReturnType<typeof useLogin>;
  sendResetPasswordLinkQuery: ReturnType<typeof useSendResetPasswordLink>;
  resetPasswordQuery: ReturnType<typeof useResetPassword>;
  verifyEmailQuery: ReturnType<typeof useVerifyEmail>;
  resendVerificationCodeQuery: ReturnType<typeof useResendVerificationCode>;
  
  user: User | null;
  userType: UserType | null;
  userLoading: boolean;
  

  isNonprofit: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextProps>(undefined!);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const isPendingAuthChangeCheck = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  

  const [user, setUser] = useState<User | null>(null);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [userLoading, setUserLoading] = useState(false);

  const logout = useCallback(() => {
    void supabaseClient.auth.signOut();

    setUser(null);
    setUserType(null);
  }, []);


  const getUserData = useCallback(async (authUser: any) => {
    if (!authUser) return;
    
    try {
      setUserLoading(true);
      
      const { data: userData, error } = await supabaseClient
        .from('users')
        .select('*')
        .eq('auth_id', authUser.id) 
        .maybeSingle();

      if (error) {
        console.error('Error fetching user data:', error);
        return;
      }

      setUser(userData);
      setUserType(userData?.user_type);
    } catch (err) {
      console.error('Error in getUserData:', err);
    } finally {
      setUserLoading(false);
    }
  }, []);

  const loginQuery = useLogin();
  const sendResetPasswordLinkQuery = useSendResetPasswordLink();
  const resetPasswordQuery = useResetPassword();
  const verifyEmailQuery = useVerifyEmail();
  const resendVerificationCodeQuery = useResendVerificationCode();

  const checkData = useCallback(async (session: Session | null) => {
    if (isPendingAuthChangeCheck.current) return;

    isPendingAuthChangeCheck.current = true;

    if (!session) {
      setIsLoading(false);
      setSession(null);
      setUser(null);
      setUserType(null);
      isPendingAuthChangeCheck.current = false;
      return;
    }

    setIsLoading(false);
    setSession(session);
    

    await getUserData(session.user);
    
    isPendingAuthChangeCheck.current = false;
  }, [getUserData]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      checkData(session);
    });

    return () => {
      subscription.unsubscribe();
      setIsLoading(true);
      setSession(null);
      setUser(null);
      setUserType(null);
    };
  }, [checkData]);


  const isNonprofit = userType === 'nonprofit';
  const isAuthenticated = !!session && !!user;

  return (
    <AuthContext.Provider
      value={{

        session,
        supabaseClient,
        logout,
        isLoading,
        loginQuery,
        resetPasswordQuery,
        sendResetPasswordLinkQuery,
        verifyEmailQuery,
        resendVerificationCodeQuery,
        user,
        userType,
        userLoading,
        isNonprofit,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuthContext(): AuthContextProps {
  const context = useContext(AuthContext);

  if (typeof context === 'undefined') {
    throw new Error('useAuthContext should be used within the AuthContext provider!');
  }

  return context;
}

export const useUser = () => {
  const { user, userType, userLoading: loading } = useAuthContext();
  return { user, userType, loading };
};